import React, { DetailedHTMLProps, HTMLAttributes } from 'react';
import classnames from 'classnames';
import {
  cmxArtifacts,
  cmxTypeguards,
  commonEnums
} from '@codametrix/ui-common';

import { FieldNames } from '../../lang/case.lang';
import {
  buildClickHandler,
  displayNoEvidenceAvailable
} from './case-evidence-common';

const { isNgram, isCategorical, isUnknownEvidence } = cmxTypeguards;
const { CaseArtifactTypes } = commonEnums;

const getCode = (code: CMxAPI.MedicalCode) => {
  if ('procedure_code' in code) {
    return code.procedure_code;
  } else if ('diagnosis_code' in code) {
    return code.diagnosis_code;
  } else if ('modifier_code' in code) {
    return code.modifier_code;
  }
};

export type CaseEvidenceProps = {
  evidenceManager: CMxCommonApp.EvidenceManager;
  medicalCode: CMxAPI.MedicalCode;
  artifact: CMxArtifacts.EnhancedCaseArtifacts;
  codeType: string;
  onSelection: CMxCommonApp.OnFeatureSelection;
} & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const CaseEvidenceTable = (props: CaseEvidenceProps) => {
  const {
    medicalCode,
    artifact,
    onSelection,
    evidenceManager,
    codeType,
    ...rest
  } = props;

  const evidenceForCode = medicalCode.evidence ?? [];

  const fromSource = artifact.type === CaseArtifactTypes.CLINICAL;
  const classes = [
    rest.className,
    'case-evidence-table',
    'col-artifacts',
    `case-evidence-${codeType.toLowerCase()}`,
    {
      'case-evidence-soure': fromSource,
      'case-has-no-evidence': !evidenceForCode.length
    }
  ];

  return (
    //@ts-ignore
    <div
      {...rest}
      className={classnames(classes)}
      key={`${artifact.identifier}-${medicalCode.uuid}-case-evidence-table`}>
      <div className="col-code"></div>
      <div className="col-description">
        <h5 className="evidence-table-descriptor">
          Why we selected {getCode(medicalCode)}
        </h5>
        {/* the check for imported means that the evidence table does not show */}
        {!evidenceForCode.length || artifact.meta?.imported === true
          ? displayNoEvidenceAvailable(artifact)
          : displayEvidenceList(
              evidenceForCode,
              evidenceManager,
              artifact,
              medicalCode,
              onSelection
            )}
      </div>
    </div>
  );
};

const displayEvidenceList = (
  evidence: CMxAPI.UnionEvidence[],
  evidenceManager: CMxCommonApp.EvidenceManager,
  artifact: CMxAPI.CaseArtifacts,
  medicalCode: CMxAPI.MedicalCode,
  onSelection: CMxCommonApp.OnFeatureSelection
) => {
  const handleClick = buildClickHandler(onSelection, evidence);

  const listItems = evidence.map((singleEvidence, index) => {
    const feature = cmxArtifacts.getFeature(singleEvidence);
    let fieldProp;
    if (isNgram(feature)) {
      fieldProp = `${feature.field}`;
    } else if (isCategorical(feature)) {
      fieldProp = `${feature.field}`;
    } else {
      // this is bogus, but really
      // we shouldn't be in here.
      fieldProp = 'undefined';
    }
    const key = `${fieldProp}-${index}-${singleEvidence.type}`;

    const linkable = cmxArtifacts.isLinkable(fieldProp);
    const featureSelection: CMxArtifacts.FeatureSelection = {
      evidence: singleEvidence,
      artifactId: artifact.artifact_id,
      code: medicalCode,
      codeType: 'something',
      display: true
    };
    const isActive = evidenceManager.hasSelectedFeature(featureSelection);

    const fieldName = FieldNames[fieldProp] || fieldProp || 'Unknown field';

    const styleClasses = [
      `case-evidence-item`,
      { 'case-evidence-linked': linkable },
      { 'case-evidence-active': isActive },
      { 'sentence-highlight': isNgram(feature) },
      { 'categorical-highlight': isCategorical(feature) }
    ];

    const valueClasses = [`field-value`, 'highlighted-text', 'sensitive-data'];

    // we're currently displaying two types of features 'ngrams' and 'categorical' data.
    if (cmxArtifacts.isSubhighlight(feature)) {
      const descriptions = feature.spans;
      return descriptions.map(span => {
        return (
          <li className={classnames(styleClasses)} key={`${key}-${span.uuid}`}>
            The <span className="field-name">{fieldName}</span> contains{' '}
            <span className={classnames(valueClasses)} data-index={index}>
              {span.content?.trim()}
            </span>
            <span className="highlighter-diagnostics">
              {span.uuid?.slice(-7)} {`${span.start}-${span.end}`}
            </span>
          </li>
        );
      });
    } else if (cmxArtifacts.isBlockHighlight(feature)) {
      const description = cmxArtifacts.getDescription(feature);
      return (
        <li className={classnames(styleClasses)} key={key}>
          The <span className="field-name">{fieldName}</span> is{' '}
          <span className={classnames(valueClasses)} data-index={index}>
            {description}
          </span>
        </li>
      );
    }
    return false;
  });

  // test for unknown evidence
  const hasUnknown = evidence.some(isUnknownEvidence);
  if (hasUnknown) {
    listItems.unshift(
      <li className={'case-evidence-item'} key={'unknown-evidence-item'}>
        <span className="unknown-evidence">
          Some information was found that is not displayable.
        </span>
      </li>
    );
  }

  return (
    <ol
      className="case-evidence-list"
      onClick={handleClick}
      key={`${medicalCode.uuid}-case-evidence-list`}>
      {listItems.flat()}
    </ol>
  );
};

CaseEvidenceTable.displayName = 'CaseEvidenceTable';

export { CaseEvidenceTable };
